import {
  ApolloClient,
  from,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { concatPagination } from '@apollo/client/utilities';
import { SentryLink } from 'apollo-link-sentry';

import { getClientValidatedEnv } from '../../config/client-env';
import { isSSR } from '../../utils/helpers';
import { logs } from '../../utils/logs';

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) =>
      logs.error(
        {
          message: `GraphQL error: (Operation: ${operation.operationName}) Message: ${message}, Location: ${locations}, Path: ${path}`,
          graphql: {
            operation: operation.operationName,
            message,
            locations,
            path,
          },
        },
        'GRAPHQL_ERROR'
      )
    );
  if (networkError) {
    logs.error(
      {
        message: `Network error: (Operation: ${operation.operationName}) ${networkError}`,
        graphql: {
          operation: operation.operationName,
          networkError,
        },
      },
      'GRAPHQL_NETWORK_ERROR'
    );
  }
});

function createApolloClient(url: string) {
  const httpLink = new HttpLink({
    uri: url,
    credentials: 'same-origin',
  });

  return new ApolloClient({
    ssrMode: isSSR(),
    link: from([
      ...(getClientValidatedEnv().NEXT_PUBLIC_PRODUCTION_ENV
        ? [
            // production
            new SentryLink({
              attachBreadcrumbs: {
                includeError: true,
                includeFetchResult: true,
                includeVariables: true,
                includeQuery: true,
              },
            }),
          ]
        : [
            // not production
          ]),
      errorLink,
      httpLink,
    ]),
    defaultOptions: {
      // SmartSearch has its own cache, so we don't want to cache anything here
      // maybe if we start using GraphQL for something else
      watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore',
      },
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
    },
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            allPosts: concatPagination(),
          },
        },
      },
    }),
  });
}

export function initializeApollo(
  name: string,
  url: string,
  initialState?: NormalizedCacheObject
) {
  const _apolloClient = createApolloClient(url);

  if (initialState) {
    _apolloClient.cache.restore(initialState);
  }

  return _apolloClient;
}

export function getApolloState(
  name: string,
  client: ApolloClient<NormalizedCacheObject>
) {
  return {
    [name]: client.cache.extract(),
  };
}
